import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('access_key', '3ecf27f1-0b06-493f-8a17-94c13339fed3');
      formBody.append('subject', 'New Quote Request - Contact Page Form');
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('service', formData.service);
      formBody.append('message', formData.message);

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section className="py-14 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-block bg-[#f67f19] text-white px-4 py-2 rounded-full font-bold text-sm mb-4 md:mb-6">
            GET IN TOUCH
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#535669] mb-3 md:mb-4">
            Request Your Free Quote
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Fill out the form below and we'll get back to you within 24 hours. Or call us now for immediate assistance!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12">
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6" data-readdy-form id="contact-form">
              <div>
                <label htmlFor="name" className="block text-sm font-bold text-[#535669] mb-2">
                  Full Name <span className="text-[#f67f19]">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 md:px-5 md:py-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#f67f19] focus:border-[#f67f19] text-sm md:text-base transition-all"
                  placeholder="John Smith"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-[#535669] mb-2">
                    Email Address <span className="text-[#f67f19]">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 md:px-5 md:py-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#f67f19] focus:border-[#f67f19] text-sm md:text-base transition-all"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-bold text-[#535669] mb-2">
                    Phone Number <span className="text-[#f67f19]">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 md:px-5 md:py-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#f67f19] focus:border-[#f67f19] text-sm md:text-base transition-all"
                    placeholder="0416 497 716"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-bold text-[#535669] mb-2">
                  Service Required <span className="text-[#f67f19]">*</span>
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 md:px-5 md:py-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#f67f19] focus:border-[#f67f19] text-sm md:text-base cursor-pointer transition-all"
                >
                  <option value="">Select a service</option>
                  <option value="Emergency Work">Emergency Work</option>
                  <option value="Ceiling Fans">Ceiling Fans</option>
                  <option value="Power Points">Power Points</option>
                  <option value="Fault Detection">Fault Detection</option>
                  <option value="Smoke Alarms">Smoke Alarms</option>
                  <option value="Lighting">Lighting</option>
                  <option value="Electrical Repairs">Electrical Repairs</option>
                  <option value="Wiring & Rewiring">Wiring & Rewiring</option>
                  <option value="Surge Protection">Surge Protection</option>
                  <option value="Switchboards">Switchboards</option>
                  <option value="Electrical Maintenance">Electrical Maintenance</option>
                  <option value="Solar Batteries">Solar Batteries</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-bold text-[#535669] mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 md:px-5 md:py-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-[#f67f19] focus:border-[#f67f19] text-sm md:text-base resize-none transition-all"
                  placeholder="Tell us about your electrical needs..."
                ></textarea>
                <div className="text-sm text-gray-500 mt-2">{formData.message.length}/500 characters</div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#f67f19] text-white px-6 py-4 md:px-8 md:py-5 rounded-full font-bold text-base md:text-lg hover:bg-[#e06e0a] transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer transform hover:scale-[1.02]"
              >
                {isSubmitting ? 'Sending...' : 'Get Your Free Quote Now'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-500 text-green-800 px-4 py-3 md:px-6 md:py-4 rounded-lg flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-xl md:text-2xl text-green-500"></i>
                  <span className="font-semibold text-sm md:text-base">Thank you! We'll contact you within 24 hours.</span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-500 text-red-800 px-4 py-3 md:px-6 md:py-4 rounded-lg flex items-center gap-3">
                  <i className="ri-error-warning-fill text-xl md:text-2xl text-red-500"></i>
                  <span className="font-semibold text-sm md:text-base">Something went wrong. Please try again or call us directly.</span>
                </div>
              )}
            </form>
          </div>

          <div className="lg:col-span-2 space-y-5 md:space-y-6">
            <div className="bg-[#535669] text-white p-6 md:p-8 rounded-xl shadow-lg">
              <h3 className="text-xl md:text-2xl font-bold mb-6 md:mb-8">Contact Information</h3>
              
              <div className="space-y-5 md:space-y-6">
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-14 md:h-14 bg-[#f67f19] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-fill text-lg md:text-2xl text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold mb-1 md:mb-2 text-base md:text-lg">Phone</div>
                    <a href="tel:0416497716" className="text-[#f67f19] hover:text-white transition-colors text-base md:text-lg cursor-pointer">
                      0416 497 716
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-14 md:h-14 bg-[#f67f19] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-fill text-lg md:text-2xl text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold mb-1 md:mb-2 text-base md:text-lg">Email</div>
                    <a href="mailto:urbanconnect@mail.com" className="text-[#f67f19] hover:text-white transition-colors break-all cursor-pointer text-sm md:text-base">
                      urbanconnect@mail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-14 md:h-14 bg-[#f67f19] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-fill text-lg md:text-2xl text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold mb-1 md:mb-2 text-base md:text-lg">Business Hours</div>
                    <div className="text-gray-300 space-y-1 text-sm md:text-base">
                      <div>Mon - Fri: 7:00 AM - 6:00 PM</div>
                      <div>Sat: 8:00 AM - 4:00 PM</div>
                      <div>Sun: Emergency Only</div>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-14 md:h-14 bg-[#f67f19] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-2-fill text-lg md:text-2xl text-white"></i>
                  </div>
                  <div>
                    <div className="font-bold mb-1 md:mb-2 text-base md:text-lg">24/7 Emergency</div>
                    <div className="text-gray-300 text-sm md:text-base">
                      Available for urgent electrical issues
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#f67f19] to-[#e06e0a] text-white p-6 md:p-8 rounded-xl shadow-lg">
              <div className="flex items-start gap-3 md:gap-4 mb-3 md:mb-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-gift-fill text-lg md:text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl md:text-2xl font-bold mb-2">Special Offer!</h3>
                  <p className="text-sm md:text-lg leading-relaxed">
                    Book online today and receive <strong className="text-white">$50 OFF</strong> your service plus a <strong className="text-white">FREE call out</strong>!
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs md:text-sm bg-white/10 px-3 py-2 md:px-4 md:py-3 rounded-lg">
                <i className="ri-time-line text-base md:text-lg"></i>
                <span className="font-semibold">Limited time offer - Book now!</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}